import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, LockKeyhole } from "lucide-react";
import { getRoles, getSsoConfig, login } from "../lib/api";
import { homeFor } from "../lib/admin";
import { LOGIN } from "../constants/testIds/auth";
import { PRODUCT_IDENTITY, PRODUCT_TAGLINE } from "../lib/story";

export const DEMO_EMAIL = "admin@yug360.demo";
export const DEMO_PASSWORD = "DemoPass!2025";

const FALLBACK_ROLES = [
  { role: "Product Owner", email: DEMO_EMAIL, pointer: "Next human action: seal or refuse the pending MDR.", home: "/", login: "/login", logout: "/logout" },
  { role: "Tenant Admin", email: "tenant.admin@yug360.demo", pointer: "Customer admin console. Cannot authorize an MDR.", home: "/admin/tenant" },
  { role: "Platform Admin", email: "platform.admin@yug360.demo", pointer: "Control plane only. No customer data plane.", home: "/admin/platform" },
];

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  const autoDemo = useRef(false);
  const [email, setEmail] = useState(DEMO_EMAIL);
  const [password, setPassword] = useState(DEMO_PASSWORD);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [sso, setSso] = useState({ enabled: true, registration: true, mode: "local" });
  const [roles, setRoles] = useState(FALLBACK_ROLES);

  useEffect(() => {
    getSsoConfig()
      .then(({ data }) => setSso(data))
      .catch(() => {});
    getRoles()
      .then(({ data }) => { if (data?.roles?.length) setRoles(data.roles); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ssoError = params.get("sso_error");
    if (ssoError) setError(`SSO did not complete (${ssoError}).`);
    const preset = params.get("email");
    if (preset) setEmail(preset);
  }, [location.search]);

  const signIn = async (nextEmail, nextPassword) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await login(String(nextEmail || "").trim(), nextPassword);
      const ok = await onLogin();
      if (!ok) {
        setError("Signed in, but the session could not be verified.");
        return;
      }
      const params = new URLSearchParams(location.search);
      const next = params.get("next");
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      navigate(safeNext || homeFor(data), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const demo = new URLSearchParams(location.search).get("demo") === "1";
    if (!demo || autoDemo.current) return;
    autoDemo.current = true;
    setEmail(DEMO_EMAIL);
    setPassword(DEMO_PASSWORD);
    signIn(DEMO_EMAIL, DEMO_PASSWORD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const submit = (e) => {
    e.preventDefault();
    signIn(email, password);
  };

  const unavailable = (label) => (e) => {
    e.preventDefault();
    setNotice(`${label} is not in this POC. Use Create an account or Continue with SSO.`);
  };

  const ssoHref = sso.mode === "oidc" && sso.oidc_start ? sso.oidc_start : "/login/sso";

  return (
    <main className="login-screen login-screen-auth" data-testid="login-page">
      <div className="login-grid" />
      <section className="login-hero">
        <div className="eyebrow"><span className="signal" /> NEXTPROMIND · YUG360-MIND</div>
        <h1>Sign in to the<br /><em>Northstar</em> workspace.</h1>
        <p>Email and password, SSO, or create a new user. JWT httpOnly cookies · bcrypt · lockout after 5 failures. Seeded operators stay in place.</p>
        <p className="muted small-note">Each row is a role. Same demo password. Logout is /logout. Pointer text is a governance cue — See details after sign-in.</p>
        <Link data-testid="login-back-landing" className="text-button" to="/">Back to product</Link>
      </section>
      <form className="login-panel" onSubmit={submit}>
        <div className="brand-mark">YUG<span>360</span><small>— MIND</small></div>
        <p className="kicker">{PRODUCT_IDENTITY.toUpperCase()}</p>
        <h2>Sign in</h2>
        <p className="muted">{PRODUCT_TAGLINE} NRG-01 sample workspace · Model=None.</p>
        <label htmlFor="login-email">Email
          <input
            id="login-email"
            data-testid={LOGIN.emailInput}
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label htmlFor="login-password">Password
          <input
            id="login-password"
            data-testid={LOGIN.passwordInput}
            type="password"
            name="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <div className="login-actions">
          <button data-testid={LOGIN.submitButton} className="primary-button full" type="submit" disabled={loading}>
            {loading ? "Signing in…" : "Sign in"} <ArrowUpRight size={16} />
          </button>
          {sso.enabled && (
            sso.mode === "oidc" && sso.oidc_start ? (
              <a data-testid="login-sso-button" className="secondary-button full" href={ssoHref}>
                Continue with SSO
              </a>
            ) : (
              <Link data-testid="login-sso-button" className="secondary-button full" to="/login/sso">
                Continue with SSO
              </Link>
            )
          )}
          <button
            type="button"
            data-testid="login-open-demo"
            className="secondary-button full"
            disabled={loading}
            onClick={() => signIn(DEMO_EMAIL, DEMO_PASSWORD)}
          >
            Open Northstar workspace
          </button>
        </div>
        <div className="login-links">
          <a href="/login" data-testid={LOGIN.forgotPasswordLink} onClick={unavailable("Password reset")}>Forgot password</a>
          {sso.registration ? (
            <Link data-testid={LOGIN.registerLink} to="/register">Create an account</Link>
          ) : (
            <a href="/login" data-testid={LOGIN.registerLink} onClick={unavailable("Registration")}>Create an account</a>
          )}
        </div>
        {notice && <div className="login-notice" data-testid="login-poc-notice">{notice}</div>}
        {error && <div data-testid="login-error" className="error-message">{error}</div>}
        <p className="muted small-note" data-testid="login-demo-hint">Workspace demo: {DEMO_EMAIL} · logout /logout · same password for every seeded role</p>
        <div className="login-accounts login-role-rows" data-testid="login-admin-accounts">
          {roles.map((account) => (
            <button
              key={account.email || account.role}
              type="button"
              className="login-account"
              data-testid={`login-role-${String(account.role || "").toLowerCase().replaceAll(" ", "-")}`}
              disabled={loading}
              onClick={() => signIn(account.email, DEMO_PASSWORD)}
            >
              <strong>{account.role}</strong>
              <small>{account.email} · login {account.login || "/login"} · logout {account.logout || "/logout"}</small>
              <span>{account.pointer}</span>
            </button>
          ))}
        </div>
        <div className="login-footer"><LockKeyhole size={14} /> Tenant-isolated · Human-authorized · Model=None ready</div>
      </form>
    </main>
  );
}
